import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, TextField, FormControl,
  Select, MenuItem, IconButton, Typography, Box, Alert,
  Divider, Table, TableHead, TableRow, TableCell, TableBody,
  Tooltip, Switch, Chip, Paper, alpha, useTheme,
  InputAdornment, CircularProgress, Autocomplete,
} from '@mui/material';
import {
  Add, DeleteOutline, ExpandMore, ExpandLess,
  CalendarMonth, Sell,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';

import feePlanService from '../../services/feePlanService';
import feeCategoryService from '../../services/feeCategoryService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import {
  FeeCategory,
  FeePlan,
  FeePlanInput,
  FeePlanItemInput,
  FeeFrequency,
  FEE_FREQUENCY_LABELS,
  FEE_FREQUENCY_INSTALLMENTS,
} from '../../types/fees';
import { Session, SessionClass, SectionInstance } from '../../types/sessions';
import SearchableSelect from '../common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';
import ConfirmationDialog, { ConfirmFacts } from '../common/ConfirmationDialog';

interface FeePlanFormDialogProps {
  open: boolean;
  editing: FeePlan | null;
  defaultSessionId?: string;
  onClose: () => void;
  onSaved: () => void;
}

interface EditableItem extends FeePlanItemInput {
  key: string;             // stable UI key so React reconciles cleanly
}

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const toDateInput = (v?: string | Date | null): string => {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  return format(d, 'yyyy-MM-dd');
};

const snapForwardToBillingDay = (d: Date, billingDay: number): Date => {
  const day = Math.min(28, Math.max(1, billingDay));
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  // Anchor at 12:00 UTC so the calendar date is stable across timezones.
  const inMonth = new Date(Date.UTC(y, m, day, 12, 0, 0, 0));
  if (inMonth.getTime() >= d.getTime()) return inMonth;
  return new Date(Date.UTC(y, m + 1, day, 12, 0, 0, 0));
};

/**
 * Mirrors the backend `generateSchedule` (feePlanController.ts) so what the
 * admin sees in the plan editor's schedule preview is exactly what the
 * server will persist. Snaps every installment to the school's configured
 * billing day (default 1st of month).
 */
const generateSchedule = (
  total: number,
  frequency: FeeFrequency,
  from: Date,
  to: Date,
  billingDay: number = 1,
): { dueDate: string; amount: number }[] => {
  const count = FEE_FREQUENCY_INSTALLMENTS[frequency] ?? 1;
  const per = Math.round((total / count) * 100) / 100;
  const remainder = Math.round((total - per * count) * 100) / 100;

  const strideMonths =
    frequency === 'monthly'   ? 1
    : frequency === 'quarterly' ? 3
    : frequency === 'termly'    ? 4
    : 0;

  const anchor = snapForwardToBillingDay(from, billingDay);
  const result: { dueDate: string; amount: number }[] = [];
  for (let i = 0; i < count; i++) {
    let due: Date;
    if (strideMonths === 0) {
      due = anchor;
    } else {
      due = new Date(Date.UTC(
        anchor.getUTCFullYear(),
        anchor.getUTCMonth() + strideMonths * i,
        Math.min(28, Math.max(1, billingDay)),
        12, 0, 0, 0,
      ));
    }
    if (due.getTime() > to.getTime()) due = to;
    const amount = i === count - 1 ? Math.round((per + remainder) * 100) / 100 : per;
    result.push({ dueDate: toDateInput(due), amount });
  }
  return result;
};

const idOf = (v: any): string | undefined => {
  if (!v) return undefined;
  return typeof v === 'string' ? v : v._id;
};

const FeePlanFormDialog: React.FC<FeePlanFormDialogProps> = ({
  open, editing, defaultSessionId, onClose, onSaved,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { feeBillingDay } = useAuth();

  // Catalogs
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [categories, setCategories] = useState<FeeCategory[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sessionId, setSessionId] = useState('');
  // classIds — empty = school-wide, length 1 = single class (section allowed),
  // length ≥ 2 = multi-class (section disabled).
  const [classIds, setClassIds] = useState<string[]>([]);
  const singleClassId = classIds.length === 1 ? classIds[0] : '';
  const [sectionInstanceId, setSectionInstanceId] = useState(''); // '' = all sections
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [items, setItems] = useState<EditableItem[]>([]);
  const [expandedItemKey, setExpandedItemKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [realigning, setRealigning] = useState(false);
  const [confirmRealign, setConfirmRealign] = useState(false);

  const realignSchedules = async () => {
    if (!editing) return;
    setRealigning(true);
    try {
      const r = await feePlanService.realignPlanSchedules(editing._id);
      enqueueSnackbar(
        `Installment dates realigned. ${r.assignmentsUpdated} assignment${r.assignmentsUpdated === 1 ? '' : 's'} updated.`,
        { variant: 'success' },
      );
      setConfirmRealign(false);
      onSaved();
      onClose();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not realign schedules', { variant: 'error' });
    } finally {
      setRealigning(false);
    }
  };
  const [error, setError] = useState<string | null>(null);

  const isEditing = Boolean(editing);
  const isLocked = Boolean(editing?.lockedAt);
  const activeSession = sessions.find(s => s._id === sessionId);

  // ─── Catalog loads ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCatalogs(true);
    (async () => {
      try {
        const [ss, cls, cats] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
          feeCategoryService.getFeeCategories(),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
        setCategories(cats);
      } catch {
        if (!cancelled) enqueueSnackbar('Could not load pickers', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingCatalogs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, enqueueSnackbar]);

  // Sections only apply when the plan covers exactly one class.
  useEffect(() => {
    if (!open || !sessionId || !singleClassId) {
      setSections([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await sectionInstancesApi.list({ sessionId, classId: singleClassId });
        if (!cancelled) setSections(list);
      } catch {
        if (!cancelled) setSections([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, singleClassId]);

  // ─── Prefill from `editing` or fresh defaults ─────────────────────────────

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setDescription(editing.description ?? '');
      setSessionId(idOf(editing.sessionId) ?? '');
      setClassIds((editing.classIds ?? []).map((c: any) => idOf(c)).filter(Boolean) as string[]);
      setSectionInstanceId(idOf(editing.sectionInstanceId) ?? '');
      setEffectiveFrom(toDateInput(editing.effectiveFrom));
      setEffectiveTo(toDateInput(editing.effectiveTo ?? null));
      setItems((editing.items ?? []).map(it => ({
        key: uid(),
        feeCategoryId: idOf(it.feeCategoryId) || '',
        amount: it.amount,
        frequency: it.frequency,
        isOptional: it.isOptional,
        schedule: it.schedule.map(s => ({ dueDate: toDateInput(s.dueDate), amount: s.amount })),
      })));
    } else {
      setName('');
      setDescription('');
      setSessionId(defaultSessionId ?? '');
      setClassIds([]);
      setSectionInstanceId('');
      setEffectiveFrom('');
      setEffectiveTo('');
      setItems([]);
    }
    setExpandedItemKey(null);
    setError(null);
  }, [open, editing, defaultSessionId]);

  // When session changes on a NEW plan, auto-fill effectiveFrom/To from the
  // session's start/end. Skip when editing to preserve user-chosen values.
  useEffect(() => {
    if (!open || isEditing || !activeSession) return;
    if (!effectiveFrom) setEffectiveFrom(toDateInput(activeSession.startDate));
    if (!effectiveTo)   setEffectiveTo(toDateInput(activeSession.endDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditing, activeSession?._id]);

  // Section is only valid when exactly one class is picked. Any change from
  // that state clears the section field.
  useEffect(() => {
    if (classIds.length !== 1 && sectionInstanceId) setSectionInstanceId('');
  }, [classIds, sectionInstanceId]);

  // Track the effective-window we last used to auto-generate schedules so we
  // can detect *real* user changes (vs. the initial hydration when opening the
  // dialog / editing an existing plan).
  const lastAppliedWindowRef = useRef<{ from: string; to: string } | null>(null);
  useEffect(() => {
    if (!open) {
      lastAppliedWindowRef.current = null;
      return;
    }
    // First observation after open — record it and don't touch the schedule.
    // This prevents nuking the persisted schedule when editing an existing plan.
    if (lastAppliedWindowRef.current === null) {
      lastAppliedWindowRef.current = { from: effectiveFrom, to: effectiveTo };
      return;
    }
    if (
      lastAppliedWindowRef.current.from === effectiveFrom &&
      lastAppliedWindowRef.current.to === effectiveTo
    ) return;
    lastAppliedWindowRef.current = { from: effectiveFrom, to: effectiveTo };

    if (isLocked || items.length === 0) return;
    const from = effectiveFrom ? new Date(effectiveFrom) : null;
    const to   = effectiveTo   ? new Date(effectiveTo)   : (activeSession ? new Date(activeSession.endDate) : null);
    if (!from || !to || isNaN(from.getTime()) || isNaN(to.getTime())) return;

    setItems(prev => prev.map(i => ({
      ...i,
      schedule: generateSchedule(i.amount, i.frequency, from, to, feeBillingDay),
    })));
    enqueueSnackbar('Installment dates recomputed for the new effective window', { variant: 'info' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, effectiveFrom, effectiveTo]);

  const scheduleWindow = useMemo(() => {
    const from = effectiveFrom ? new Date(effectiveFrom) : null;
    const to   = effectiveTo   ? new Date(effectiveTo)   : (activeSession ? new Date(activeSession.endDate) : null);
    return { from, to };
  }, [effectiveFrom, effectiveTo, activeSession]);

  // ─── Item helpers ─────────────────────────────────────────────────────────

  const usedCategoryIds = useMemo(
    () => new Set(items.map(i => i.feeCategoryId).filter(Boolean)),
    [items],
  );

  const availableCategories = useCallback((currentValue?: string) =>
    categories.filter(c => c._id === currentValue || !usedCategoryIds.has(c._id)),
    [categories, usedCategoryIds],
  );

  const addItem = () => {
    const first = categories.find(c => !usedCategoryIds.has(c._id));
    if (!first) {
      enqueueSnackbar('No more fee categories available — add one in Settings first', { variant: 'info' });
      return;
    }
    const key = uid();
    const item: EditableItem = {
      key,
      feeCategoryId: first._id,
      amount: 0,
      frequency: 'annual',
      isOptional: first.isOptional,
      schedule: [],
    };
    setItems(prev => [...prev, item]);
    setExpandedItemKey(null);
  };

  const removeItem = (key: string) => {
    setItems(prev => prev.filter(i => i.key !== key));
    setExpandedItemKey(k => (k === key ? null : k));
  };

  const patchItem = (key: string, patch: Partial<EditableItem>) => {
    setItems(prev => prev.map(i => {
      if (i.key !== key) return i;
      const next = { ...i, ...patch };
      const shouldRegenerate =
        (patch.amount !== undefined && patch.amount !== i.amount) ||
        (patch.frequency !== undefined && patch.frequency !== i.frequency);
      if (shouldRegenerate && scheduleWindow.from && scheduleWindow.to) {
        next.schedule = generateSchedule(next.amount, next.frequency, scheduleWindow.from, scheduleWindow.to, feeBillingDay);
      }
      return next;
    }));
  };

  const patchInstallment = (
    itemKey: string,
    idx: number,
    patch: Partial<{ dueDate: string; amount: number }>,
  ) => {
    setItems(prev => prev.map(i => {
      if (i.key !== itemKey) return i;
      const schedule = i.schedule ? [...i.schedule] : [];
      schedule[idx] = { ...schedule[idx], ...patch };
      return { ...i, schedule };
    }));
  };

  const resetItemSchedule = (key: string) => {
    if (!scheduleWindow.from || !scheduleWindow.to) return;
    setItems(prev => prev.map(i =>
      i.key === key
        ? { ...i, schedule: generateSchedule(i.amount, i.frequency, scheduleWindow.from!, scheduleWindow.to!, feeBillingDay) }
        : i,
    ));
  };

  /**
   * One-click migration for a plan whose items were computed under the old
   * spread algorithm (or a different billing day). Recomputes every item's
   * schedule from the current effective window + current school billing day.
   * Only exposed on unlocked plans (the button is hidden otherwise).
   */
  const regenerateAllSchedules = () => {
    const { from, to } = scheduleWindow;
    if (!from || !to) return;
    setItems(prev => prev.map(i => ({
      ...i,
      schedule: generateSchedule(i.amount, i.frequency, from, to, feeBillingDay),
    })));
    enqueueSnackbar('Every item’s installment dates were recomputed. Review before saving.', { variant: 'info' });
  };

  // ─── Totals ────────────────────────────────────────────────────────────────

  const totals = useMemo(() => {
    let annual = 0;
    let optional = 0;
    for (const it of items) {
      const itemTotal = it.schedule && it.schedule.length > 0
        ? it.schedule.reduce((s, sc) => s + (Number(sc.amount) || 0), 0)
        : Number(it.amount) || 0;
      annual += itemTotal;
      if (it.isOptional) optional += itemTotal;
    }
    return {
      annual: Math.round(annual * 100) / 100,
      optional: Math.round(optional * 100) / 100,
      required: Math.round((annual - optional) * 100) / 100,
    };
  }, [items]);

  // ─── Save ─────────────────────────────────────────────────────────────────

  const validate = (): string | null => {
    if (!name.trim()) return 'Plan name is required';
    if (!sessionId) return 'Pick a session';
    if (sectionInstanceId && classIds.length !== 1) return 'Section is only valid when the plan covers exactly one class';
    if (!effectiveFrom) return 'Effective from date is required';
    if (items.length === 0) return 'Add at least one fee line item';

    const { from, to } = scheduleWindow;
    for (let idx = 0; idx < items.length; idx++) {
      const it = items[idx];
      if (!it.feeCategoryId) return 'Every line item needs a fee category';
      if (!(Number(it.amount) >= 0)) return 'Amounts must be zero or positive';

      // If the schedule was auto-generated (all installments still at the
      // snap-computed dates) OR is empty, check that the window can fit it.
      // For custom schedules the admin has edited, we trust their dates.
      if (from && to) {
        const count = FEE_FREQUENCY_INSTALLMENTS[it.frequency] ?? 1;
        const strideMonths = it.frequency === 'monthly' ? 1
          : it.frequency === 'quarterly' ? 3
          : it.frequency === 'termly' ? 4
          : 0;
        const anchor = snapForwardToBillingDay(from, feeBillingDay);
        const lastOffset = strideMonths * (count - 1);
        const lastInstallment = new Date(Date.UTC(
          anchor.getUTCFullYear(),
          anchor.getUTCMonth() + lastOffset,
          Math.min(28, Math.max(1, feeBillingDay)),
          12, 0, 0, 0,
        ));
        if (lastInstallment.getTime() > to.getTime()) {
          const need = format(lastInstallment, 'MMM d, yyyy');
          const has = format(to, 'MMM d, yyyy');
          return `Line ${idx + 1}: a ${it.frequency} plan needs its last installment on ${need}, but "Effective to" ends ${has}. Reduce frequency or extend the end date.`;
        }
      }
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: FeePlanInput = {
        sessionId,
        classIds,
        sectionInstanceId: sectionInstanceId || null,
        name: name.trim(),
        description: description.trim() || undefined,
        items: items.map(it => ({
          feeCategoryId: it.feeCategoryId,
          amount: Number(it.amount) || 0,
          frequency: it.frequency,
          isOptional: it.isOptional,
          schedule: it.schedule?.map(s => ({
            dueDate: s.dueDate,
            amount: Number(s.amount) || 0,
          })),
        })),
        effectiveFrom,
        effectiveTo: effectiveTo || null,
      };

      if (editing) {
        await feePlanService.updateFeePlan(editing._id, payload);
        enqueueSnackbar('Fee plan updated', { variant: 'success' });
      } else {
        await feePlanService.createFeePlan(payload);
        enqueueSnackbar('Fee plan created', { variant: 'success' });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      const msg = e?.message || 'Could not save fee plan';
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const currencyFmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          {isEditing ? 'Edit fee plan' : 'New fee plan'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          A plan lists what a cohort owes in a session. Once assigned to a student, it locks — clone it to create a new version.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loadingCatalogs && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {isLocked && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{ mb: 2 }}
            action={
              editing ? (
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  disabled={realigning}
                  onClick={() => setConfirmRealign(true)}
                >
                  {realigning ? 'Realigning…' : 'Realign installment dates'}
                </Button>
              ) : undefined
            }
          >
            This plan is in use by student assignments. It's read-only. Use <strong>Clone</strong> from the plan list to create a new editable version.
            <Box component="div" sx={{ mt: 0.5 }}>
              If installment dates look wrong for the current effective window, use <strong>Realign installment dates</strong> to recompute — amounts stay the same and every active assignment picks up the new dates.
            </Box>
          </Alert>
        )}

        <Stack spacing={2.5}>
          {/* Header block */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: 'block', mb: 1 }}>
              Plan details
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Plan name"
                  placeholder="e.g. Class V — Standard 2024-25"
                  fullWidth
                  value={name}
                  onChange={e => setName(e.target.value)}
                  disabled={isLocked}
                  autoFocus
                />
                <SearchableSelect
                  label="Session"
                  value={sessionId}
                  onChange={setSessionId}
                  options={buildSessionOptions(sessions, theme)}
                  disabled={isEditing || isLocked}
                  clearable={false}
                  fullWidth
                  minWidth={0}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  options={classes}
                  getOptionLabel={c => c.name}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  value={classes.filter(c => classIds.includes(String(c._id)))}
                  onChange={(_, next) => setClassIds(next.map(c => String(c._id)))}
                  disabled={isLocked}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, i) => (
                      <Chip
                        {...getTagProps({ index: i })}
                        key={opt._id}
                        label={opt.name}
                        size="small"
                        sx={{
                          height: 22, fontSize: '0.72rem', fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.dark,
                          '& .MuiChip-deleteIcon': { fontSize: 14, color: 'inherit', opacity: 0.7 },
                          '& .MuiChip-deleteIcon:hover': { opacity: 1 },
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Class scope"
                      placeholder={classIds.length === 0 ? 'All classes (school-wide)' : 'Add a class…'}
                      InputLabelProps={{ shrink: true }}
                      helperText={
                        classIds.length === 0
                          ? 'Empty = plan applies to every class in the session.'
                          : classIds.length === 1
                            ? 'Single-class plan — you can also pick a section below.'
                            : `${classIds.length} classes selected — sections are disabled for multi-class plans.`
                      }
                    />
                  )}
                />
                <SearchableSelect
                  label="Section scope"
                  value={sectionInstanceId}
                  onChange={setSectionInstanceId}
                  options={sections.map(s => ({ value: s._id, label: s.name }))}
                  placeholder={
                    classIds.length !== 1
                      ? 'Only for single-class plans'
                      : sections.length ? 'All sections' : 'No sections in this class'
                  }
                  disabled={isLocked || classIds.length !== 1 || sections.length === 0}
                  fullWidth
                  minWidth={0}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Effective from"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={effectiveFrom}
                  onChange={e => setEffectiveFrom(e.target.value)}
                  disabled={isLocked}
                />
                <TextField
                  label="Effective to (optional)"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={effectiveTo}
                  onChange={e => setEffectiveTo(e.target.value)}
                  helperText={activeSession ? `Defaults to session end: ${format(new Date(activeSession.endDate), 'MMM d, yyyy')}` : ' '}
                  disabled={isLocked}
                />
              </Stack>

              <TextField
                label="Description"
                placeholder="Optional — internal note visible to admins only"
                fullWidth
                multiline
                minRows={2}
                value={description}
                onChange={e => setDescription(e.target.value)}
                disabled={isLocked}
              />
            </Stack>
          </Paper>

          {/* Line items */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: 'block' }}>
                  Fee line items
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Each line is one fee head (from Settings → Fee Categories) with an amount and frequency.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                {items.length >= 2 && scheduleWindow.from && scheduleWindow.to && (
                  <Tooltip title={`Recompute every item's installment dates from the current effective window using the school's billing day (day ${feeBillingDay}).`}>
                    <span>
                      <Button
                        startIcon={<CalendarMonth fontSize="small" />}
                        onClick={regenerateAllSchedules}
                        disabled={isLocked}
                        size="small"
                        variant="text"
                      >
                        Regenerate schedules
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Button
                  startIcon={<Add />}
                  onClick={addItem}
                  disabled={isLocked || categories.length === 0 || items.length >= categories.length}
                  size="small"
                  variant="outlined"
                >
                  Add line
                </Button>
              </Stack>
            </Stack>

            {items.length === 0 ? (
              <Box sx={{
                py: 4, textAlign: 'center',
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.text.primary, 0.02),
              }}>
                <Sell sx={{ fontSize: 26, color: 'text.disabled', mb: 0.75 }} />
                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.25 }}>No line items yet</Typography>
                <Typography variant="caption" color="text.secondary">
                  {categories.length === 0
                    ? 'Create fee categories first in Settings → Fee Categories.'
                    : 'Click “Add line” to pick a category and amount.'}
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ pl: 0, width: '30%' }}>Category</TableCell>
                    <TableCell align="right" sx={{ width: '15%' }}>Amount</TableCell>
                    <TableCell sx={{ width: '18%' }}>Frequency</TableCell>
                    <TableCell align="center" sx={{ width: '10%' }}>Optional</TableCell>
                    <TableCell align="center" sx={{ width: '17%' }}>Schedule</TableCell>
                    <TableCell align="right" sx={{ width: '10%', pr: 0 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it) => {
                    const expanded = expandedItemKey === it.key;
                    const optionsForRow = availableCategories(it.feeCategoryId);
                    const linked = categories.find(c => c._id === it.feeCategoryId);
                    return (
                      <React.Fragment key={it.key}>
                        <TableRow hover>
                          <TableCell sx={{ pl: 0 }}>
                            <FormControl fullWidth size="small" disabled={isLocked}>
                              <Select
                                value={it.feeCategoryId}
                                onChange={e => patchItem(it.key, { feeCategoryId: e.target.value })}
                              >
                                {optionsForRow.map(c => (
                                  <MenuItem key={c._id} value={c._id}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Typography variant="body2">{c.name}</Typography>
                                      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                                        {c.code}
                                      </Typography>
                                    </Stack>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {linked?.isOptional && !it.isOptional && (
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                Category is marked optional in Settings
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={it.amount}
                              onChange={e => patchItem(it.key, { amount: Number(e.target.value) || 0 })}
                              inputProps={{ min: 0, step: 100, style: { textAlign: 'right' } }}
                              disabled={isLocked}
                              sx={{ width: '100%' }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small" disabled={isLocked}>
                              <Select
                                value={it.frequency}
                                onChange={e => patchItem(it.key, { frequency: e.target.value as FeeFrequency })}
                              >
                                {(Object.keys(FEE_FREQUENCY_LABELS) as FeeFrequency[]).map(k => (
                                  <MenuItem key={k} value={k}>{FEE_FREQUENCY_LABELS[k]}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              size="small"
                              checked={it.isOptional}
                              onChange={(_, v) => patchItem(it.key, { isOptional: v })}
                              disabled={isLocked}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              onClick={() => setExpandedItemKey(k => (k === it.key ? null : it.key))}
                              endIcon={expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            >
                              {it.schedule?.length || 0} due
                            </Button>
                          </TableCell>
                          <TableCell align="right" sx={{ pr: 0 }}>
                            <Tooltip title={isLocked ? '' : 'Remove line'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeItem(it.key)}
                                  disabled={isLocked}
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        {expanded && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ py: 0, borderBottom: 0 }}>
                              <Box sx={{
                                py: 1.5, px: 2, my: 0.75, borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.info.main, 0.04),
                                border: `1px dashed ${alpha(theme.palette.info.main, 0.25)}`,
                              }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                  <CalendarMonth fontSize="small" color="info" />
                                  <Typography variant="body2" fontWeight={600}>Installment schedule</Typography>
                                  <Box sx={{ flex: 1 }} />
                                  <Button size="small" onClick={() => resetItemSchedule(it.key)} disabled={isLocked}>
                                    Auto-generate
                                  </Button>
                                </Stack>
                                {(it.schedule ?? []).length === 0 ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Enter an amount and frequency — installments are auto-generated across the effective window.
                                  </Typography>
                                ) : (
                                  <Table size="small">
                                    <TableBody>
                                      {(it.schedule ?? []).map((inst, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell sx={{ pl: 0, width: 40 }}>
                                            <Typography variant="caption" color="text.secondary">#{idx + 1}</Typography>
                                          </TableCell>
                                          <TableCell>
                                            <TextField
                                              size="small"
                                              type="date"
                                              value={inst.dueDate}
                                              onChange={e => patchInstallment(it.key, idx, { dueDate: e.target.value })}
                                              disabled={isLocked}
                                              InputLabelProps={{ shrink: true }}
                                            />
                                          </TableCell>
                                          <TableCell align="right" sx={{ pr: 0 }}>
                                            <TextField
                                              size="small"
                                              type="number"
                                              value={inst.amount}
                                              onChange={e => patchInstallment(it.key, idx, { amount: Number(e.target.value) || 0 })}
                                              inputProps={{ min: 0, step: 100, style: { textAlign: 'right' } }}
                                              disabled={isLocked}
                                              InputProps={{ startAdornment: <InputAdornment position="start">Amt</InputAdornment> }}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {items.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Chip
                    label={`Optional total: ${currencyFmt(totals.optional)}`}
                    variant="outlined"
                    size="small"
                    sx={{ borderColor: alpha(theme.palette.warning.main, 0.4), color: theme.palette.warning.dark }}
                  />
                  <Chip
                    label={`Required total: ${currencyFmt(totals.required)}`}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={`Plan total: ${currencyFmt(totals.annual)}`}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
              </>
            )}
          </Paper>
        </Stack>

        {error && (
          <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={save}
          variant="contained"
          disableElevation
          disabled={saving || isLocked}
        >
          {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Create plan'}
        </Button>
      </DialogActions>

      <ConfirmationDialog
        open={confirmRealign}
        onClose={() => setConfirmRealign(false)}
        onConfirm={realignSchedules}
        loading={realigning}
        title="Realign installment dates?"
        message="Due dates are recomputed for this plan's effective window and pushed to every active student assignment on it. Amounts and totals stay the same — only due dates move."
        confirmText="Realign dates"
        confirmColor="warning"
      >
        <ConfirmFacts items={[
          { label: 'Effective from', value: effectiveFrom || '—' },
          { label: 'Effective to', value: effectiveTo || '—' },
        ]} />
      </ConfirmationDialog>
    </Dialog>
  );
};

export default FeePlanFormDialog;
